/**
 * Apollo Client Debug Wrapper
 *
 * This wrapper enhances Apollo Client's query/mutation methods to automatically
 * track GraphQL operations for session debugging purposes.
 */

import {
  sessionDebugger,
  DEBUG_CATEGORIES,
  SESSION_LOAD_STEPS,
} from './session-debug';

export function wrapApolloClient(client) {
  if (!sessionDebugger.isEnabled) {
    return client;
  }

  // Store original methods
  const originalQuery = client.query.bind(client);
  const originalMutate = client.mutate.bind(client);

  // Wrap query method
  client.query = async (options) => {
    const queryName =
      options.query?.definitions?.[0]?.name?.value || 'UnknownQuery';
    const startTime = performance.now();

    try {
      // Track query start
      sessionDebugger.trackApolloQuery(
        queryName,
        options.variables,
        null,
        null
      );

      // Track specific session-related queries
      if (queryName === 'PrefetchSession') {
        sessionDebugger.trackStep(SESSION_LOAD_STEPS.PREFETCH_START, {
          sessionKey: options.variables?.sessionKey,
          fetchPolicy: options.fetchPolicy,
        });
      }

      // Execute original query
      const result = await originalQuery(options);
      const duration = performance.now() - startTime;

      // Track query completion
      sessionDebugger.trackApolloQuery(
        queryName,
        options.variables,
        result,
        null
      );
      sessionDebugger.trackPerformance(`apollo_query_${queryName}`, duration, {
        queryName,
        variables: options.variables,
        hasData: !!result.data,
        dataSize: result.data ? JSON.stringify(result.data).length : 0,
      });

      // Track specific session-related query completions
      if (queryName === 'PrefetchSession') {
        if (result.data?.SessionByKey?.items?.length > 0) {
          sessionDebugger.trackStep(SESSION_LOAD_STEPS.PREFETCH_SUCCESS, {
            sessionId: result.data.SessionByKey.items[0].id,
            sessionName: result.data.SessionByKey.items[0].name,
            sessionStatus: result.data.SessionByKey.items[0].status,
            teamCount:
              result.data.SessionByKey.items[0].sessionTeams?.items?.length ||
              0,
          });
        } else {
          sessionDebugger.trackStep(SESSION_LOAD_STEPS.PREFETCH_ERROR, {
            error: 'No session items returned',
            resultLength: result.data?.SessionByKey?.items?.length || 0,
          });
        }
      }

      return result;
    } catch (error) {
      const duration = performance.now() - startTime;

      // Track query error
      sessionDebugger.trackApolloQuery(
        queryName,
        options.variables,
        null,
        error
      );
      sessionDebugger.trackError(
        error,
        `Apollo query: ${queryName}`,
        DEBUG_CATEGORIES.APOLLO
      );

      // Track specific session-related query errors
      if (queryName === 'PrefetchSession') {
        sessionDebugger.trackStep(SESSION_LOAD_STEPS.PREFETCH_ERROR, {
          error: error.message,
          errorType: error.name,
          networkError: error.networkError?.message,
          graphQLErrors: error.graphQLErrors?.map((e) => e.message),
        });
      }

      sessionDebugger.trackPerformance(
        `apollo_query_error_${queryName}`,
        duration,
        {
          queryName,
          error: error.message,
        }
      );

      throw error;
    }
  };

  // Wrap mutate method (less relevant for session loading, but useful for completeness)
  client.mutate = async (options) => {
    const mutationName =
      options.mutation?.definitions?.[0]?.name?.value || 'UnknownMutation';
    const startTime = performance.now();

    try {
      sessionDebugger.trackApolloQuery(
        mutationName,
        options.variables,
        null,
        null
      );

      const result = await originalMutate(options);
      const duration = performance.now() - startTime;

      sessionDebugger.trackApolloQuery(
        mutationName,
        options.variables,
        result,
        null
      );
      sessionDebugger.trackPerformance(
        `apollo_mutation_${mutationName}`,
        duration,
        {
          mutationName,
          variables: options.variables,
        }
      );

      return result;
    } catch (error) {
      const duration = performance.now() - startTime;

      sessionDebugger.trackApolloQuery(
        mutationName,
        options.variables,
        null,
        error
      );
      sessionDebugger.trackError(
        error,
        `Apollo mutation: ${mutationName}`,
        DEBUG_CATEGORIES.APOLLO
      );
      sessionDebugger.trackPerformance(
        `apollo_mutation_error_${mutationName}`,
        duration,
        {
          mutationName,
          error: error.message,
        }
      );

      throw error;
    }
  };

  return client;
}

export default wrapApolloClient;
